// src/features/plan/RecommendationCard.jsx
//
// PLAN-RECOMMENDATIONS-TAB: Single recommendation card.
//
// Shows: rank badge, title, why-line, source-lens chips
// (STRATEGY/LEASE/PRESSURE/PLAN), priority label, confidence type,
// expandable receipt (rule_id + trigger_facts), and curation actions
// (editor/admin only).
//
// Resurfaced cards show an "Escalated" badge in trigger_facts.resurfaced.

import React, { useState, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { fmtDate } from "../../utils/format.js";

// ─── Constants ────────────────────────────────────────────────────────────────

const LENS_STYLE = {
  STRATEGY: { color: T.purple,        bg: T.purpleBg },
  LEASE:    { color: T.amberDark,     bg: T.utilAmber },
  PRESSURE: { color: T.ragRed,        bg: T.ragRedBg  },
  PLAN:     { color: T.blue,          bg: T.blueSoft  },
};

const PRIORITY_LABEL = {
  act_now:      { label: "Act now",       color: T.ragRed    },
  this_quarter: { label: "This quarter",  color: T.amberDark },
  plan_ahead:   { label: "Plan ahead",    color: T.mid       },
};

const DISMISS_REASON_LABELS = {
  not_relevant:    "Not relevant",
  already_handled: "Already handled",
  disagree:        "Disagree with signal",
  bad_timing:      "Bad timing",
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function LensChip({ lens }) {
  const s = LENS_STYLE[lens] ?? { color: T.mid, bg: T.lightGray };
  return (
    <span
      style={{
        display:      "inline-block",
        fontSize:     T.fsXxs,
        fontWeight:   T.fwBold,
        color:        s.color,
        background:   s.bg,
        borderRadius: 4,
        padding:      "1px 5px",
      }}
    >
      {lens}
    </span>
  );
}

function PriorityLabel({ priority }) {
  const p = PRIORITY_LABEL[priority] ?? { label: priority, color: T.mid };
  return (
    <span style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: p.color }}>
      {p.label}
    </span>
  );
}

/**
 * @dev-only Renders the raw rule ID and trigger facts JSON for engineering inspection.
 * Gated behind localStorage "ripple_dev_mode" === "1" — never shown to ordinary users.
 */
function FactsReceipt({ ruleId, triggerFacts }) {
  return (
    <div
      style={{
        marginTop:    10,
        padding:      "8px 10px",
        background:   T.lightGray,
        borderRadius: 6,
        fontSize:     T.fsXs,
        color:        T.mid,
        fontFamily:   "monospace",
        whiteSpace:   "pre-wrap",
        wordBreak:    "break-all",
      }}
    >
      <div style={{ fontWeight: T.fwBold, marginBottom: 4, color: T.dark }}>
        Rule: {ruleId}
      </div>
      {JSON.stringify(triggerFacts, null, 2)}
    </div>
  );
}

// ─── Original engine values receipt ──────────────────────────────────────────
// Shown below FactsReceipt when curator edits exist, so the original engine
// suggestions remain accessible even after title/priority/why changes.

function OriginalValuesReceipt({ editProvenance }) {
  // Reconstruct original values from the first provenance entry's "before" fields.
  const firstEntry = editProvenance[0];
  if (!firstEntry?.fields) return null;

  const originals = {};
  for (const [field, change] of Object.entries(firstEntry.fields)) {
    if (change && "before" in change) originals[field] = change.before;
  }

  if (Object.keys(originals).length === 0) return null;

  return (
    <div
      style={{
        marginTop:    6,
        padding:      "6px 10px",
        background:   T.lightGray,
        borderRadius: 6,
        fontSize:     T.fsXs,
        color:        T.mid,
      }}
    >
      <div style={{ fontWeight: T.fwBold, marginBottom: 4, color: T.dark }}>
        Original engine suggestion
      </div>
      {originals.title && (
        <div><span style={{ color: T.mid }}>Title:</span> {originals.title}</div>
      )}
      {originals.priority && (
        <div><span style={{ color: T.mid }}>Priority:</span> {originals.priority}</div>
      )}
      {"why" in originals && (
        <div><span style={{ color: T.mid }}>Why:</span> {originals.why ?? <em>none</em>}</div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * Replace any market ID tokens (keys of nameMap) found in a text string with
 * their friendly names. Matching is case-insensitive and whole-word to avoid
 * partial substring collisions.
 *
 * @param {string}        text
 * @param {Map<string,string>} nameMap  — marketId (lowercase) → friendly name
 * @returns {string}
 */
function resolveMarketNames(text, nameMap) {
  if (!text || !nameMap || nameMap.size === 0) return text;
  let result = text;
  for (const [id, name] of nameMap) {
    // Match the raw ID as a whole word, case-insensitively.
    const pattern = new RegExp(`\\b${id.replace(/[-]/g, "[-]")}\\b`, "gi");
    result = result.replace(pattern, name);
  }
  return result;
}

/**
 * @param {object}   props
 * @param {number}   props.rank         — display rank (1-based)
 * @param {object}   props.rec          — recommendation row (camelCase from server)
 * @param {boolean}  props.canEdit      — editor+ role
 * @param {Map}      [props.nameMap]    — marketId (lowercase) → friendly name; for
 *                                        render-time resolution of stored candidates
 * @param {Function} props.onAccept
 * @param {Function} props.onDismiss    — (id, reason) => void
 * @param {Function} props.onSnooze     — (id, until) => void
 * @param {Function} props.onEdit       — (id, fields) => void
 * @param {Function} props.onUnpublish
 * @param {boolean}  [props.showPublished]  — true when rendered in published section
 * @param {string}   [props.testIdPrefix]
 */
export function RecommendationCard({
  rank,
  rec,
  canEdit,
  nameMap,
  onAccept,
  onDismiss,
  onSnooze,
  onEdit,
  onUnpublish,
  showPublished = false,
  testIdPrefix  = "rec-card",
}) {
  const [expanded,        setExpanded]        = useState(false);
  const [showDismiss,     setShowDismiss]     = useState(false);
  const [showSnooze,      setShowSnooze]      = useState(false);
  const [showEdit,        setShowEdit]        = useState(false);
  const [dismissReason,   setDismissReason]   = useState("");
  const [snoozeUntil,     setSnoozeUntil]     = useState("");
  const [editTitleVal,    setEditTitleVal]    = useState(rec.title ?? "");
  const [editPriorityVal, setEditPriorityVal] = useState(rec.priority ?? "");
  const [editWhyVal,      setEditWhyVal]      = useState(rec.why ?? "");
  const [error,           setError]           = useState(null);

  const facts        = rec.triggerFacts   ?? {};
  const lenses       = rec.sourceLenses   ?? [];
  const resurfaced   = facts.resurfaced === true;
  const editProvenance = Array.isArray(rec.editProvenance) ? rec.editProvenance : [];

  // Dev-only escape hatch: set localStorage.ripple_dev_mode = "1" to reveal raw rule payload.
  const isDevMode = typeof localStorage !== "undefined" && localStorage.getItem("ripple_dev_mode") === "1";
  // Show the expand toggle only when there is something meaningful to reveal.
  const hasExpandableContent = editProvenance.length > 0 || isDevMode;

  const priorityInfo = PRIORITY_LABEL[rec.priority] ?? { label: rec.priority, color: T.mid };

  // Fallback "why" text when the DB row has no why (pre-engine-deterministic rows,
  // or when a curator intentionally cleared the field).  Mirrors buildDeterministicWhy
  // in the engine so every card always shows a meaningful explanation.
  function aOrAn(word) {
    return /^[aeiou]/i.test(word ?? "") ? "An" : "A";
  }

  function deriveFallbackWhy(ruleId, f) {
    if (!ruleId) return null;
    if (ruleId.startsWith("LEASE_CLOCK:")) {
      const cdType = (f.soonestCdType ?? "critical date").replace(/_/g, " ");
      const days   = f.soonestDaysOut != null ? ` (${f.soonestDaysOut} days)` : "";
      const date   = f.soonestDate ? ` on ${fmtDate(f.soonestDate)}` : "";
      return `${aOrAn(cdType)} ${cdType} is due${date}${days}; a decision is needed now to avoid forfeiture.`;
    }
    if (ruleId.startsWith("DIRECTIVE_NO_ACTION:")) {
      const title   = f.directiveTitle ?? "This directive";
      const markets = Array.isArray(f.unactedMarkets) && f.unactedMarkets.length > 0
        ? ` in ${f.unactedMarkets.join(", ")}` : "";
      return `"${title}" is approved but has no linked plan action${markets}.`;
    }
    if (ruleId.startsWith("CAPACITY_WALL:")) {
      const quarter = f.wallLabel ?? "an upcoming quarter";
      const hc      = f.projectedHc != null ? ` (${f.projectedHc} projected)` : "";
      return `This market will exceed its space capacity in ${quarter}${hc}.`;
    }
    if (ruleId.startsWith("CONVERGENCE:")) {
      const directive = f.directiveTitle ?? "An approved directive";
      const quarter   = f.wallLabel ?? "an upcoming quarter";
      return `"${directive}" expands into this market, which also hits its capacity wall in ${quarter}.`;
    }
    return null;
  }
  const nm = nameMap ?? new Map();
  const displayTitle = resolveMarketNames(rec.title, nm);
  const rawWhyText   = rec.why ?? deriveFallbackWhy(rec.ruleId, facts);
  const whyText      = resolveMarketNames(rawWhyText, nm);

  const handleDismit = useCallback(async () => {
    if (!dismissReason) { setError("Please select a reason."); return; }
    setError(null);
    try {
      await onDismiss(rec.id, dismissReason);
      setShowDismiss(false);
    } catch (err) {
      setError(err.message);
    }
  }, [dismissReason, rec.id, onDismiss]);

  const handleSnooze = useCallback(async () => {
    if (!snoozeUntil) { setError("Please select a date."); return; }
    setError(null);
    try {
      await onSnooze(rec.id, snoozeUntil);
      setShowSnooze(false);
    } catch (err) {
      setError(err.message);
    }
  }, [snoozeUntil, rec.id, onSnooze]);

  const handleEdit = useCallback(async () => {
    if (!editTitleVal.trim()) { setError("Title cannot be empty."); return; }
    setError(null);
    // Build editFields with only the changed values.
    const fields = {};
    if (editTitleVal.trim() !== rec.title)    fields.title    = editTitleVal.trim();
    if (editPriorityVal && editPriorityVal !== rec.priority) fields.priority = editPriorityVal;
    if (editWhyVal !== (rec.why ?? ""))       fields.why      = editWhyVal || null;
    if (Object.keys(fields).length === 0) { setShowEdit(false); return; }
    try {
      await onEdit(rec.id, fields);
      setShowEdit(false);
    } catch (err) {
      setError(err.message);
    }
  }, [editTitleVal, editPriorityVal, editWhyVal, rec.id, rec.title, rec.priority, rec.why, onEdit]);

  return (
    <div
      data-testid={`${testIdPrefix}-${rec.id}`}
      style={{
        border:       `1px solid ${T.border}`,
        borderRadius: 8,
        background:   T.white,
        padding:      "12px 14px",
        marginBottom: 8,
      }}
    >
      {/* ── Header row ─────────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        {/* Rank badge */}
        <div
          style={{
            flexShrink:   0,
            width:        22,
            height:       22,
            borderRadius: "50%",
            background:   T.lightGray,
            display:      "flex",
            alignItems:   "center",
            justifyContent: "center",
            fontSize:     T.fsXs,
            fontWeight:   T.fwBold,
            color:        T.mid,
            marginTop:    1,
          }}
        >
          {rank}
        </div>

        {/* Main body */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Title row */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginBottom: 2 }}>
            <span style={{ fontWeight: T.fwSemibold, fontSize: T.fsBase, color: T.dark }}>
              {displayTitle}
            </span>
            {resurfaced && (
              <span
                data-testid={`${testIdPrefix}-${rec.id}-escalated`}
                style={{
                  fontSize:     T.fsXxs,
                  fontWeight:   T.fwBold,
                  color:        T.ragRed,
                  background:   T.ragRedBg,
                  borderRadius: 4,
                  padding:      "1px 5px",
                }}
              >
                Escalated
              </span>
            )}
          </div>

          {/* Why line */}
          {whyText && (
            <div style={{ fontSize: T.fsSm, color: T.mid, marginBottom: 6, lineHeight: 1.45 }}>
              {whyText}
            </div>
          )}

          {/* Meta row: lenses + priority + confidence */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
            {lenses.map((l) => <LensChip key={l} lens={l} />)}
            <span style={{ color: T.borderDark, fontSize: T.fsXs }}>·</span>
            <PriorityLabel priority={rec.priority} />
            <span style={{ color: T.borderDark, fontSize: T.fsXs }}>·</span>
            <span style={{ fontSize: T.fsXs, color: T.mid }}>{rec.confidenceType}</span>
            {editProvenance.length > 0 && (
              <>
                <span style={{ color: T.borderDark, fontSize: T.fsXs }}>·</span>
                <span style={{ fontSize: T.fsXs, color: T.mid, fontStyle: "italic" }}>Edited</span>
              </>
            )}
          </div>
        </div>

        {/* Expand/collapse receipt toggle — only when there is expandable content */}
        {hasExpandableContent && (
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-expand`}
            onClick={() => setExpanded((v) => !v)}
            style={{
              flexShrink:   0,
              background:   "transparent",
              border:       "none",
              cursor:       "pointer",
              fontSize:     T.fsSm,
              color:        T.mid,
              padding:      "2px 4px",
            }}
          >
            {expanded ? "▲" : "▼"}
          </button>
        )}
      </div>

      {/* ── Receipt (expanded) ──────────────────────────────────────────── */}
      {expanded && (
        <>
          {/* FactsReceipt is dev-only — hidden from ordinary users */}
          {isDevMode && <FactsReceipt ruleId={rec.ruleId} triggerFacts={facts} />}
          {editProvenance.length > 0 && (
            <OriginalValuesReceipt editProvenance={editProvenance} />
          )}
        </>
      )}

      {/* ── Error banner ────────────────────────────────────────────────── */}
      {error && (
        <div style={{ marginTop: 6, fontSize: T.fsSm, color: T.ragRed }}>
          {error}
        </div>
      )}

      {/* ── Curation actions (candidate queue — editor/admin) ────────────── */}
      {canEdit && !showPublished && (
        <div style={{ marginTop: 10, display: "flex", flexWrap: "wrap", gap: 6 }}>
          {/* Accept */}
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-accept`}
            onClick={async () => { try { await onAccept(rec.id); } catch (err) { setError(err.message); } }}
            style={actionBtnStyle(T.greenDeep)}
          >
            Accept
          </button>

          {/* Edit */}
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-edit`}
            onClick={() => { setShowEdit((v) => !v); setShowDismiss(false); setShowSnooze(false); }}
            style={actionBtnStyle(T.blue)}
          >
            Edit
          </button>

          {/* Snooze */}
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-snooze`}
            onClick={() => { setShowSnooze((v) => !v); setShowDismiss(false); setShowEdit(false); }}
            style={actionBtnStyle(T.mid)}
          >
            Snooze
          </button>

          {/* Dismiss */}
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-dismiss`}
            onClick={() => { setShowDismiss((v) => !v); setShowSnooze(false); setShowEdit(false); }}
            style={actionBtnStyle(T.ragRed)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* ── Unpublish (published section — editor/admin) ─────────────────── */}
      {canEdit && showPublished && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            data-testid={`${testIdPrefix}-${rec.id}-unpublish`}
            onClick={async () => { try { await onUnpublish(rec.id); } catch (err) { setError(err.message); } }}
            style={actionBtnStyle(T.mid)}
          >
            Unpublish
          </button>
        </div>
      )}

      {/* ── Dismiss sub-panel ───────────────────────────────────────────── */}
      {showDismiss && (
        <div
          data-testid={`${testIdPrefix}-${rec.id}-dismiss-panel`}
          style={{
            marginTop:  8,
            padding:    "8px 10px",
            background: T.lightGray,
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
            Reason for dismissal (required)
          </div>
          <select
            value={dismissReason}
            onChange={(e) => setDismissReason(e.target.value)}
            style={{ fontSize: T.fsSm, padding: "4px 6px", borderRadius: 4, border: `1px solid ${T.border}` }}
          >
            <option value="">Select reason…</option>
            {Object.entries(DISMISS_REASON_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={handleDismit}
            style={{ ...actionBtnStyle(T.ragRed), marginLeft: 8 }}
          >
            Confirm dismiss
          </button>
        </div>
      )}

      {/* ── Snooze sub-panel ────────────────────────────────────────────── */}
      {showSnooze && (
        <div
          data-testid={`${testIdPrefix}-${rec.id}-snooze-panel`}
          style={{
            marginTop:  8,
            padding:    "8px 10px",
            background: T.lightGray,
            borderRadius: 6,
          }}
        >
          <div style={{ fontSize: T.fsSm, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 6 }}>
            Snooze until
          </div>
          <input
            type="date"
            value={snoozeUntil}
            onChange={(e) => setSnoozeUntil(e.target.value)}
            style={{ fontSize: T.fsSm, padding: "4px 6px", borderRadius: 4, border: `1px solid ${T.border}` }}
          />
          <button
            type="button"
            onClick={handleSnooze}
            style={{ ...actionBtnStyle(T.mid), marginLeft: 8 }}
          >
            Confirm snooze
          </button>
        </div>
      )}

      {/* ── Edit sub-panel (title + priority + why) ─────────────────────── */}
      {showEdit && (
        <div
          data-testid={`${testIdPrefix}-${rec.id}-edit-panel`}
          style={{
            marginTop:    8,
            padding:      "8px 10px",
            background:   T.lightGray,
            borderRadius: 6,
            display:      "flex",
            flexDirection: "column",
            gap:          8,
          }}
        >
          {/* Title */}
          <div>
            <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
              Title
            </div>
            <input
              type="text"
              data-testid={`${testIdPrefix}-${rec.id}-edit-title`}
              value={editTitleVal}
              onChange={(e) => setEditTitleVal(e.target.value)}
              style={{
                fontSize:  T.fsSm,
                padding:   "4px 8px",
                borderRadius: 4,
                border:    `1px solid ${T.border}`,
                width:     "100%",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Priority */}
          <div>
            <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
              Priority
            </div>
            <select
              data-testid={`${testIdPrefix}-${rec.id}-edit-priority`}
              value={editPriorityVal}
              onChange={(e) => setEditPriorityVal(e.target.value)}
              style={{ fontSize: T.fsSm, padding: "4px 6px", borderRadius: 4, border: `1px solid ${T.border}` }}
            >
              <option value="act_now">Act now</option>
              <option value="this_quarter">This quarter</option>
              <option value="plan_ahead">Plan ahead</option>
            </select>
          </div>

          {/* Why (override LLM / engine text) */}
          <div>
            <div style={{ fontSize: T.fsXs, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 3 }}>
              Explanation (why)
            </div>
            <textarea
              data-testid={`${testIdPrefix}-${rec.id}-edit-why`}
              value={editWhyVal}
              onChange={(e) => setEditWhyVal(e.target.value)}
              rows={2}
              placeholder="Leave blank to remove explanation"
              style={{
                fontSize:  T.fsSm,
                padding:   "4px 8px",
                borderRadius: 4,
                border:    `1px solid ${T.border}`,
                width:     "100%",
                boxSizing: "border-box",
                resize:    "vertical",
              }}
            />
          </div>

          <div style={{ display: "flex", gap: 6 }}>
            <button
              type="button"
              data-testid={`${testIdPrefix}-${rec.id}-edit-save`}
              onClick={handleEdit}
              style={actionBtnStyle(T.blue)}
            >
              Save
            </button>
            <button
              type="button"
              onClick={() => { setShowEdit(false); setError(null); }}
              style={actionBtnStyle(T.mid)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Style helpers ────────────────────────────────────────────────────────────

function actionBtnStyle(color) {
  return {
    background:   "transparent",
    border:       `1px solid ${color}`,
    borderRadius: 6,
    color,
    fontSize:     T.fsSm,
    fontWeight:   T.fwSemibold,
    cursor:       "pointer",
    padding:      "4px 10px",
  };
}
